import api from './api';
import { ApiResponse, PaginatedResponse } from '../types';

export enum SubcontractorStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  BLACKLISTED = 'blacklisted',
}

export interface Qualification {
  name: string;
  certificateNo: string;
  validUntil: string;
  attachmentUrl?: string;
}

export interface CooperationHistory {
  projectId: string;
  projectName: string;
  workItemIds: string[];
  startDate: string;
  endDate?: string;
  totalAmount: number;
  evaluation?: number;
  evaluationComment?: string;
}

export interface SubcontractorUsageSummary {
  subcontractCostCount: number;
  cooperationProjectCount: number;
}

export interface Subcontractor {
  _id: string;
  name: string;
  shortName?: string;
  contactPerson: string;
  phone: string;
  email?: string;
  address?: string;
  businessLicense?: string;
  qualifications?: Qualification[];
  cooperationHistory: CooperationHistory[];
  totalProjects: number;
  totalAmount: number;
  averageEvaluation: number;
  status: SubcontractorStatus;
  blacklistReason?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SubcontractorDetail extends Subcontractor {
  usage: SubcontractorUsageSummary;
}

export interface SubcontractorQueryParams {
  keyword?: string;
  status?: SubcontractorStatus;
  page?: number;
  limit?: number;
}

export interface CreateSubcontractorData {
  name: string;
  shortName?: string;
  contactPerson: string;
  phone: string;
  email?: string;
  address?: string;
  businessLicense?: string;
  qualifications?: Qualification[];
  status?: SubcontractorStatus;
  blacklistReason?: string;
}

export interface UpdateSubcontractorData extends Partial<CreateSubcontractorData> {}

export class SubcontractorService {
  static async getSubcontractors(
    params: SubcontractorQueryParams = {}
  ): Promise<PaginatedResponse<Subcontractor>> {
    return api.get('/subcontractors', { params }) as Promise<PaginatedResponse<Subcontractor>>;
  }

  static async getSubcontractorById(id: string): Promise<ApiResponse<SubcontractorDetail>> {
    return api.get(`/subcontractors/${id}`) as Promise<ApiResponse<SubcontractorDetail>>;
  }

  static async createSubcontractor(data: CreateSubcontractorData): Promise<ApiResponse<Subcontractor>> {
    return api.post('/subcontractors', data) as Promise<ApiResponse<Subcontractor>>;
  }

  static async updateSubcontractor(
    id: string,
    data: UpdateSubcontractorData
  ): Promise<ApiResponse<Subcontractor>> {
    return api.put(`/subcontractors/${id}`, data) as Promise<ApiResponse<Subcontractor>>;
  }

  static async deleteSubcontractor(id: string): Promise<ApiResponse<void>> {
    return api.delete(`/subcontractors/${id}`) as Promise<ApiResponse<void>>;
  }

  static getStatusOptions(): Array<{ value: SubcontractorStatus; label: string }> {
    return [
      { value: SubcontractorStatus.ACTIVE, label: '活跃' },
      { value: SubcontractorStatus.INACTIVE, label: '停用' },
      { value: SubcontractorStatus.BLACKLISTED, label: '黑名单' },
    ];
  }

  static getStatusLabel(status: SubcontractorStatus): string {
    return this.getStatusOptions().find((item) => item.value === status)?.label || status;
  }

  static getStatusColor(status: SubcontractorStatus): string {
    switch (status) {
      case SubcontractorStatus.ACTIVE:
        return 'green';
      case SubcontractorStatus.INACTIVE:
        return 'default';
      case SubcontractorStatus.BLACKLISTED:
        return 'red';
      default:
        return 'default';
    }
  }
}
